'use client';

import { Suspense, useEffect, useState } from 'react';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import { SignIn } from '@clerk/nextjs';
import { useTranslations } from 'next-intl';
import { useAuth } from 'react-oidc-context';

import { DEFAULT_SIGN_IN_REDIRECT } from '~/env.mjs';
import { getLogger } from '~/lib/logger';
import { useEnvs } from '~/providers/envs-provider';
import { decryptData, CLIENT_OBFUSCATION_KEY } from '~/utils/encryption';

import { EmailGate } from '../_components/email-gate';

const logger = getLogger();

type SignInErrorKey = 'expired' | 'network' | 'invalid' | 'generic';

type SignInState =
  | { step: 'gate' }
  | { step: 'clerk'; email: string | undefined }
  | { step: 'cognito'; email: string | undefined };

function getErrorKey(error: Error): SignInErrorKey {
  const message = error.message.toLowerCase();

  if (message.includes('expired') || message.includes('timeout')) {
    return 'expired';
  }
  if (message.includes('network') || message.includes('fetch')) {
    return 'network';
  }
  if (message.includes('invalid') || message.includes('unauthorized')) {
    return 'invalid';
  }

  logger.error(new Error('Authentication error'), {
    className: 'CognitoSignIn',
    functionName: 'getErrorKey',
    errorMessage: error.message,
  });

  return 'generic';
}

function buildSignInUrl(
  redirect: string | null,
  provider: string | null = null,
) {
  const params = new URLSearchParams();
  if (redirect) params.set('redirect', redirect);
  if (provider) params.set('provider', provider);
  const qs = params.toString();
  return qs ? `/sign-in?${qs}` : '/sign-in';
}

function CognitoSignIn() {
  const auth = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const t = useTranslations('auth.signIn');

  useEffect(() => {
    const redirectParam = searchParams.get('redirect');
    if (redirectParam) {
      sessionStorage.setItem('redirectAfterSignIn', redirectParam);
    }
  }, [searchParams]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      const redirectTo =
        sessionStorage.getItem('redirectAfterSignIn') ??
        DEFAULT_SIGN_IN_REDIRECT;
      sessionStorage.removeItem('redirectAfterSignIn');
      router.replace(redirectTo);
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- activeNavigator can be undefined at runtime
    if (!auth.isLoading && !auth.isAuthenticated && !auth.activeNavigator) {
      auth.signinRedirect().catch((error) => {
        logger.error(new Error('Failed to redirect to sign in'), {
          className: 'CognitoSignIn',
          functionName: 'signinRedirect',
          errorMessage: error instanceof Error ? error.message : String(error),
        });
      });
    }
  }, [auth, router]);

  if (auth.error) {
    return (
      <main className="grid h-screen w-screen place-items-center">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-red-600">
            {t('error.title')}
          </h1>
          <p className="mt-2 text-gray-600">
            {t(`error.${getErrorKey(auth.error)}`)}
          </p>
          <button
            className="mt-4 rounded-md bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
            onClick={() => {
              auth.signinRedirect().catch((error) => {
                logger.error(new Error('Failed to redirect to sign in'), {
                  className: 'CognitoSignIn',
                  functionName: 'signinRedirect:retry',
                  errorMessage:
                    error instanceof Error ? error.message : String(error),
                });
              });
            }}
            type="button"
          >
            {t('error.tryAgain')}
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="grid h-screen w-screen place-items-center">
      <div className="text-center">
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
        <p className="mt-4 text-gray-600">{t('redirecting')}</p>
      </div>
    </main>
  );
}

function ClerkSignInForm({ initialEmail }: { readonly initialEmail?: string }) {
  const emailAddress =
    initialEmail ??
    (() => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- SSR check
      if (globalThis.window === undefined) return undefined;
      const savedEmail = localStorage.getItem('savedEmail');
      try {
        if (!savedEmail) {
          return undefined;
        }
        const decryptedEmail = decryptData(savedEmail, CLIENT_OBFUSCATION_KEY);
        return decryptedEmail || undefined;
      } catch (error) {
        const ctx = {
          className: 'SignIn',
          functionName: 'decryptEmail',
        };

        if (error instanceof Error) {
          logger.error(error, ctx);
        } else {
          logger.error(
            new Error(`Unexpected error type - ${String(error)}`, {
              cause: error,
            }),
          );
        }
        return undefined;
      }
    })();

  return (
    <main className="grid h-screen w-screen place-items-center pt-4">
      <SignIn
        appearance={{
          elements: {
            logoBox: 'flex items-center w-full h-auto',
            footer: 'hidden',
          },
        }}
        fallbackRedirectUrl="/appointments"
        forceRedirectUrl={
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- SSR check
          globalThis.window === undefined
            ? '/appointments'
            : (sessionStorage.getItem('redirectAfterSignIn') ?? '/appointments')
        }
        initialValues={{ emailAddress }}
      />
    </main>
  );
}

const SERVICE_UNAVAILABLE_RETRY_MS = 5000;

function ServiceUnavailableRetry({
  redirect,
}: {
  readonly redirect: string | null;
}) {
  const t = useTranslations('auth.signIn');

  useEffect(() => {
    const target =
      redirect?.startsWith('/') && !redirect.startsWith('//')
        ? redirect
        : DEFAULT_SIGN_IN_REDIRECT;
    const timer = setTimeout(() => {
      globalThis.location.replace(target);
    }, SERVICE_UNAVAILABLE_RETRY_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [redirect]);

  return (
    <main className="grid h-screen w-screen place-items-center">
      <div className="text-center">
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
        <h1 className="mt-4 text-xl font-semibold">
          {t('serviceUnavailable.title')}
        </h1>
        <p className="mt-2 text-gray-600">{t('serviceUnavailable.detail')}</p>
      </div>
    </main>
  );
}

function getInitialStep(
  enableCognitoAll: boolean,
  forceClerk: boolean,
  providerParam: string | null,
): SignInState {
  if (enableCognitoAll) return { step: 'cognito', email: undefined };
  if (providerParam === 'cognito') return { step: 'cognito', email: undefined };
  if (providerParam === 'clerk') return { step: 'clerk', email: undefined };
  if (forceClerk) return { step: 'clerk', email: undefined };
  return { step: 'gate' };
}

function SignInContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const { ENABLE_COGNITO_ALL, FORCE_EMAIL_GATE_BYPASS_TO_CLERK } = useEnvs();
  const authFailed = searchParams.get('authFailed') === '1';
  const serviceUnavailable =
    searchParams.get('error') === 'service_unavailable';
  const initialProvider =
    pathname === '/sign-in/cognito' ? 'cognito' : searchParams.get('provider');

  const [state, setState] = useState<SignInState>(
    getInitialStep(
      ENABLE_COGNITO_ALL,
      FORCE_EMAIL_GATE_BYPASS_TO_CLERK,
      initialProvider,
    ),
  );

  useEffect(() => {
    if (!authFailed) return;

    const url = buildSignInUrl(searchParams.get('redirect'), initialProvider);

    if (initialProvider === 'cognito' || ENABLE_COGNITO_ALL) {
      globalThis.location.replace(url);
      return;
    }

    if (FORCE_EMAIL_GATE_BYPASS_TO_CLERK) {
      setState({ step: 'clerk', email: undefined });
    } else {
      setState({ step: 'gate' });
    }
    router.replace(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on mount
  }, []);

  function handleProvider(provider: 'clerk' | 'cognito', email: string) {
    if (provider === 'cognito') {
      const redirect = searchParams.get('redirect');
      const base = redirect
        ? `/sign-in/cognito?redirect=${encodeURIComponent(redirect)}`
        : `/sign-in/cognito`;
      globalThis.location.replace(base);
    } else {
      setState({ step: provider, email });
    }
  }

  if (serviceUnavailable) {
    return <ServiceUnavailableRetry redirect={searchParams.get('redirect')} />;
  }

  if (state.step === 'clerk') {
    return <ClerkSignInForm initialEmail={state.email} />;
  }

  if (state.step === 'cognito') {
    return <CognitoSignIn />;
  }

  return <EmailGate onProvider={handleProvider} />;
}

export default function Page() {
  return (
    <Suspense>
      <SignInContent />
    </Suspense>
  );
}
